import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { toast } from "react-hot-toast";

const ROLES = ["ADMIN", "SUPER_ADMIN"];

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "ADMIN" });
  const [formError, setFormError] = useState("");

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => apiService.getUsers(),
  });

  const createMutation = useMutation({
    mutationFn: ({ email, name, role }) =>
      apiService.createUser(email, name, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setForm({ email: "", name: "", role: "ADMIN" });
      setShowForm(false);
      setFormError("");
      toast.success("User created");
    },
    onError: (err) => setFormError(err.message || "Failed to create user"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User removed");
    },
    onError: (err) => toast.error(err.message || "Failed to remove user"),
  });

  useEffect(() => {
    if (currentUser && currentUser.role !== "SUPER_ADMIN") {
      navigate("/dashboard", { replace: true });
    }
  }, [currentUser, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    createMutation.mutate(form);
  };

  const handleDelete = (u) => {
    if (window.confirm(`Remove ${u.email}?`)) {
      deleteMutation.mutate(u.id);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ margin: 0 }}>User Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? "Cancel" : "+ Add User"}
        </button>
      </div>

      {showForm && (
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3 style={{ margin: "0 0 16px" }}>New User</h3>
          {formError && (
            <div className="error-alert" style={{ marginBottom: "12px" }}>
              {formError}
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
          >
            <input
              type="email"
              className="form-input"
              placeholder="Email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              style={{ flex: "1 1 200px" }}
            />
            <input
              type="text"
              className="form-input"
              placeholder="Full name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              style={{ flex: "1 1 160px" }}
            />
            <select
              className="form-input"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              style={{ flex: "0 1 160px" }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r === "SUPER_ADMIN" ? "Super Admin" : "Admin"}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating…" : "Create"}
            </button>
          </form>
        </div>
      )}

      {isLoading ? (
        <p>Loading…</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "10px 12px" }}>Email</th>
              <th style={{ padding: "10px 12px" }}>Name</th>
              <th style={{ padding: "10px 12px" }}>Role</th>
              <th style={{ padding: "10px 12px" }}>Created</th>
              <th style={{ padding: "10px 12px" }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                style={{ borderBottom: "1px solid #f3f4f6" }}
              >
                <td style={{ padding: "10px 12px" }}>{u.email}</td>
                <td style={{ padding: "10px 12px" }}>{u.name}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: "12px",
                      background:
                        u.role === "SUPER_ADMIN" ? "#dbeafe" : "#f3f4f6",
                      color: u.role === "SUPER_ADMIN" ? "#1d4ed8" : "#374151",
                    }}
                  >
                    {u.role === "SUPER_ADMIN" ? "Super Admin" : "Admin"}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: "13px" }}>
                  {u.createdAt
                    ? new Date(u.createdAt).toLocaleDateString()
                    : "—"}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {u.id !== currentUser?.userId && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: "12px", padding: "4px 10px", color: "#dc2626" }}
                      onClick={() => handleDelete(u)}
                      disabled={deleteMutation.isPending}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UsersPage;
